import { QuestionRegulatoryData, Questionnaire, QuestionnaireQuestion } from "../../Models/Surveys/SurveyDataModel";
import { BaseRegulationLogicManager } from "../BaseRegulationLogicManager";
import ResourcesConstants from "../ResourceConstants";
import { ComplianceScoreResult, InventoryDetailsResponse } from "../../StateControllers/Survey/SurveyResponse/SurveyResponseModels";

export class PfasCustomLogicManager extends BaseRegulationLogicManager {
    public static PfasRegulationId = "a7dbbb1e-b5ad-49b3-9b6a-cb737e6233d6";
    private readonly trueAsBit = 1;
    private readonly falseAsBit = 0;
    private readonly emptyArrayJson = "[]";

    private readonly q1Id: string = "1";
    private readonly q2Id: string = "2";
    private readonly q3Id: string = "3";
    private readonly q4Id: string = "4";
    private readonly q5Id: string = "5";
    private readonly q6Id: string = "6";
    private readonly q7Id: string = "7";
    private readonly q8Id: string = "8";

    private readonly fluorine: string = "Fluorine";
    private readonly chlorine: string = "Chlorine";
    private readonly bromine: string = "Bromine";
    private readonly iodine: string = "Iodine";
    private readonly unknown: string = "Unknown";
    private readonly noOrganohalogensInt: string = "No organohalogens were intentionally added";
    private readonly noOrganohalogensUnint: string = "No organohalogens were unintentionally added";

    constructor(questions: QuestionnaireQuestion[], overallQuestionnaire: Questionnaire, questionRegulatoryData: QuestionRegulatoryData[], selectedInventory: InventoryDetailsResponse | null, complianceScoreResult: ComplianceScoreResult | null) {
        super(questions, overallQuestionnaire, questionRegulatoryData, selectedInventory, complianceScoreResult);
    }

    onCheckBoxResponseChange = (question: QuestionnaireQuestion, answers: string[]) => {
        if (question.id === this.q1Id) {
            if (answers.includes(this.unknown)) {
                answers = [this.unknown];
                question.disabledValues = [this.fluorine, this.chlorine, this.bromine, this.iodine, this.noOrganohalogensInt];
            } else if (answers.includes(this.noOrganohalogensInt)) {
                answers = [this.noOrganohalogensInt];
                question.disabledValues = [this.fluorine, this.chlorine, this.bromine, this.iodine, this.unknown];
            } else {
                question.disabledValues = [];
            }
        }
        else if (question.id === this.q2Id) {
            if (answers.includes(this.unknown)) {
                answers = [this.unknown];
                question.disabledValues = [this.fluorine, this.chlorine, this.bromine, this.iodine, this.noOrganohalogensUnint];
            } else if (answers.includes(this.noOrganohalogensUnint)) {
                answers = [this.noOrganohalogensUnint];
                question.disabledValues = [this.fluorine, this.chlorine, this.bromine, this.iodine, this.unknown];
            } else {
                question.disabledValues = [];
            }
        }

        question.value = JSON.stringify(question.predefinedAnswers.map(p => answers.some(a => a == p.nativeAnswer) ? this.trueAsBit : this.falseAsBit))
        return answers;
    }

    public getRadioColumns(): number[] {
        return [2, 3];
    }

    protected getAdditionalQuestionIndicesToHide = (question: QuestionnaireQuestion, questionIndex: number, indicesToHideOrShow: boolean[]) => {
        const isFluorineSelected = this.isFluorineSelected();
        const isUnknownSelected = this.isUnknownSelected();

        let childQuestionIndex1;
        let childQuestionIndex2;

        switch (question.id) {
            case this.q1Id:
            case this.q2Id: {
                childQuestionIndex1 = this._questions.findIndex(q => q.id === this.q3Id);
                childQuestionIndex2 = this._questions.findIndex(q => q.id === this.q7Id);

                indicesToHideOrShow[childQuestionIndex1] = !isFluorineSelected;
                indicesToHideOrShow = this.getAdditionalQuestionIndicesToHide(this._questions[childQuestionIndex1], childQuestionIndex1, indicesToHideOrShow);

                indicesToHideOrShow[childQuestionIndex2] = !(isUnknownSelected && !isFluorineSelected);
                indicesToHideOrShow = this.getAdditionalQuestionIndicesToHide(this._questions[childQuestionIndex2], childQuestionIndex2, indicesToHideOrShow);
                break;
            }
            case this.q3Id: {
                childQuestionIndex1 = this._questions.findIndex(q => q.id === this.q4Id);
                indicesToHideOrShow[childQuestionIndex1] = !(isFluorineSelected && (question.value.length > 0 && question.value !== this.emptyArrayJson));
                indicesToHideOrShow = this.getAdditionalQuestionIndicesToHide(this._questions[childQuestionIndex1], childQuestionIndex1, indicesToHideOrShow);
                break;
            }
            case this.q4Id: {
                childQuestionIndex1 = this._questions.findIndex(q => q.id === this.q5Id);
                childQuestionIndex2 = this._questions.findIndex(q => q.id === this.q6Id);

                const isParentQuestionDisplayed = isFluorineSelected && (question.value.length > 0 && question.value !== this.emptyArrayJson);

                indicesToHideOrShow[childQuestionIndex1] = !(isParentQuestionDisplayed && question.value.toLowerCase() === ResourcesConstants.NativeAnswerTexts.Tested.toLowerCase());
                indicesToHideOrShow[childQuestionIndex2] = !isParentQuestionDisplayed;
                break;
            }
            case this.q7Id: {
                childQuestionIndex1 = this._questions.findIndex(q => q.id === this.q8Id);

                const isParentQuestionDisplayed = isUnknownSelected && !isFluorineSelected;

                indicesToHideOrShow[childQuestionIndex1] = !(isParentQuestionDisplayed && question.value && JSON.parse(question.value).containsAnyOf([1]));
                break;
            }
        }

        return indicesToHideOrShow;
    }

    protected customIsQuestionRequired = (question: QuestionnaireQuestion) => {
        const isFluorineSelected = this.isFluorineSelected();
        const isUnknownSelected = this.isUnknownSelected();
        let parentQuestionIndex;

        switch (question.id) {
            case this.q3Id: {
                return isFluorineSelected;
            }
            case this.q4Id: {
                parentQuestionIndex = this._questions.findIndex(q => q.id === this.q3Id);
                return isFluorineSelected && (this._questions[parentQuestionIndex].value.length > 0 && this._questions[parentQuestionIndex].value !== this.emptyArrayJson);
            }
            case this.q7Id: {
                return isUnknownSelected && !isFluorineSelected;
            }
            case this.q8Id: {
                parentQuestionIndex = this._questions.findIndex(q => q.id === this.q7Id);
                let isParentDisplayed = isUnknownSelected && !isFluorineSelected;
                return isParentDisplayed && this._questions[parentQuestionIndex].value && JSON.parse(this._questions[parentQuestionIndex].value).containsAnyOf([1]);
            }
        }

        return false;
    }

    private isNthValueChecked(value: string, optionNumber: number) : boolean {
        if (!value || value === ResourcesConstants.NativeAnswerTexts.NoResponse) {
            return false;
        }

        const answerList: number[] = JSON.parse(value);

        return answerList[optionNumber] === this.trueAsBit;
    }

    private isFluorineSelected() : boolean {
        const firstQuestionValue = this._questions.find(q => q.id === this.q1Id)?.value ?? "";
        const secondQuestionValue = this._questions.find(q => q.id === this.q2Id)?.value ?? "";

        return this.isNthValueChecked(firstQuestionValue, 0) || this.isNthValueChecked(secondQuestionValue, 0);
    }

    private isUnknownSelected() : boolean {
        const firstQuestionValue = this._questions.find(q => q.id === this.q1Id)?.value ?? "";
        const secondQuestionValue = this._questions.find(q => q.id === this.q2Id)?.value ?? "";

        return this.isNthValueChecked(firstQuestionValue, 5) || this.isNthValueChecked(secondQuestionValue, 5);
    }
}

